import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { BookOpen, ChevronDown, Cpu, CreditCard, Info, Layers, Plus, X, Zap } from 'lucide-react'
import { fetchLlmSettings, updateLlmSettings, updateModDLlmSettings, upsertLlmModel, deleteLlmModel, fetchReadCreditConfig, updateReadCreditConfig } from '../api/client'
import type { LlmModelSource, LlmProvider, LlmSettings, ModDLlmConfig, ReadCreditConfig } from '../api/types'

type PanelId = 'modes' | 'defaults' | 'modD' | 'catalog' | 'credit'

const sourceLabel = (source?: LlmModelSource) => {
  if (source === 'app_settings') return 'Uygulama ayarları'
  if (source === 'env') return '.env / ortam'
  return 'Varsayılan'
}

const sourceDetail = (key: string, source?: LlmModelSource) => {
  if (source === 'app_settings') return `${key} — uygulama ayarları (veritabanı)`
  if (source === 'env') return `${key} — ortam değişkeni (.env)`
  return `${key} — kod varsayılanı`
}

const providerName = (provider: LlmProvider) => (provider === 'gemini' ? 'Gemini' : 'OpenAI')

const providerApi = (provider: LlmProvider, openaiApi: string) =>
  provider === 'gemini' ? 'Gemini generateContent' : openaiApi

const extractError = (err: unknown, fallback: string) => {
  if (typeof err === 'object' && err && 'response' in err) {
    const response = (err as { response?: { data?: { error?: string } } }).response
    if (response?.data?.error) return response.data.error
  }
  if (typeof err === 'object' && err && 'message' in err) {
    const message = (err as { message?: string }).message
    if (message) return message
  }
  return fallback
}

type GuideRow = {
  step: string
  value: string
  api?: string
  source: string
  edit: string
  editPanel?: PanelId
}

type ModeGuide = {
  key: string
  name: string
  description: string
  icon: ReactNode
  rows: GuideRow[]
}

function buildModeGuide(settings: LlmSettings): ModeGuide[] {
  const { openai, gemini } = settings.providers
  const defaultProvider = settings.default_provider
  const defaultModel = defaultProvider === 'gemini' ? gemini.current_model : openai.current_model
  const defaultSource = `LLM_DEFAULT_PROVIDER (${sourceLabel(settings.effective?.provider_source)}) + ${
    defaultProvider === 'gemini' ? 'GEMINI_MODEL' : 'OPENAI_MODEL'
  } (${sourceLabel(settings.effective?.source)})`
  const defaultEdit = 'Varsayılanlar panelinde sağlayıcı ve model seçip kaydedin.'

  const modD = settings.mod_d
  const modDSources = settings.mod_d_sources
  const modDSource = (stage: 'extraction' | 'grading') => {
    const key = stage === 'extraction' ? 'MOD_D_EXTRACTION' : 'MOD_D_GRADING'
    return modDSources?.[`${stage}_model`] === 'app_settings'
      ? `${key}_PROVIDER / ${key}_MODEL — uygulama ayarları (veritabanı)`
      : `Mod D ayarı kaydedilmemiş — varsayılanlardan miras (${defaultSource})`
  }

  return [
    {
      key: 'd',
      name: 'Mod D',
      description:
        'Yeni sınavlar bu modla oluşturulur. Sorular sınav oluşturulurken soru kağıdından çıkarılır ve notlamada JSON olarak prompt\'a eklenir; sınav sayfasında düzenlenip onaylanabilir.',
      icon: <Zap size={16} />,
      rows: [
        {
          step: 'Rubrik',
          value: 'Çıkarma yok — rubrik PDF\'i her notlamada eklenir',
          api: 'OpenAI: dosya olarak, Gemini: satır içi PDF olarak',
          source: 'Sınava yüklenen rubrik PDF\'i',
          edit: 'Sınav oluşturulurken yüklenir.',
        },
        {
          step: 'Soru çıkarma',
          value: modD ? `${providerName(modD.extraction_provider)} · ${modD.extraction_model}` : '—',
          api: modD ? providerApi(modD.extraction_provider, 'OpenAI Responses API') : undefined,
          source: modDSource('extraction'),
          edit: 'Mod D Ayarları panelinden değiştirilir. Diğer modları etkilemez.',
          editPanel: 'modD',
        },
        {
          step: 'Notlama',
          value: modD ? `${providerName(modD.grading_provider)} · ${modD.grading_model}` : '—',
          api: modD ? providerApi(modD.grading_provider, 'OpenAI Responses API') : undefined,
          source: modDSource('grading'),
          edit: 'Mod D Ayarları panelinden değiştirilir. Diğer modları etkilemez.',
          editPanel: 'modD',
        },
      ],
    },
    {
      key: 'ab',
      name: 'Mod A/B',
      description: 'Klasik değerlendirme. Rubrik metne, sorular JSON\'a dönüştürülür. Mod B\'de soru kağıdı yoktur.',
      icon: <Layers size={16} />,
      rows: [
        {
          step: 'Rubrik çıkarma',
          value: `Gemini · ${gemini.current_model}`,
          api: 'Gemini generateContent',
          source: `Sağlayıcı kodda sabit (Gemini); model ${sourceDetail('GEMINI_MODEL', gemini.source)}`,
          edit: 'Varsayılanlar panelinde Gemini seçip modeli kaydedin. Dikkat: bu, varsayılan sağlayıcıyı da Gemini yapar.',
          editPanel: 'defaults',
        },
        {
          step: 'Soru çıkarma (sınav oluşturma)',
          value: 'OpenAI · gpt-4o',
          api: 'OpenAI Chat Completions',
          source: 'Kodda sabit',
          edit: 'Arayüzden değiştirilemez.',
        },
        {
          step: 'Soru çıkarma (soru kağıdını sonradan yükleme)',
          value: `${providerName(defaultProvider)} · ${defaultModel}`,
          api: 'Hata olursa diğer sağlayıcı denenir',
          source: defaultSource,
          edit: defaultEdit,
          editPanel: 'defaults',
        },
        {
          step: 'Notlama',
          value: `${providerName(defaultProvider)} · ${defaultModel}`,
          api: providerApi(defaultProvider, 'OpenAI Chat Completions'),
          source: defaultSource,
          edit: defaultEdit,
          editPanel: 'defaults',
        },
      ],
    },
    {
      key: 'c',
      name: 'Mod C',
      description: 'Rubrik ve soru kağıdı PDF olarak doğrudan LLM\'e gönderilir. Rubrik ve soru çıkarma adımı yoktur.',
      icon: <BookOpen size={16} />,
      rows: [
        {
          step: 'Rubrik ve soru kağıdı',
          value: 'Çıkarma yok — iki PDF de her notlamada dosya olarak eklenir',
          source: 'Sınava yüklenen PDF\'ler',
          edit: 'Sınav oluşturulurken yüklenir.',
        },
        {
          step: 'Notlama',
          value: `OpenAI · ${openai.current_model}`,
          api: 'OpenAI Responses API',
          source: `Sağlayıcı kodda sabit (OpenAI); model ${sourceDetail('OPENAI_MODEL', openai.source)}`,
          edit: 'Varsayılanlar panelinde OpenAI seçip modeli kaydedin. Dikkat: bu, varsayılan sağlayıcıyı da OpenAI yapar.',
          editPanel: 'defaults',
        },
      ],
    },
    {
      key: 'all',
      name: 'Tüm modlar',
      description: 'Notlama tamamlandıktan sonra oluşturulan sınıf analizi.',
      icon: <Cpu size={16} />,
      rows: [
        {
          step: 'Sınıf analizi',
          value: `${providerName(defaultProvider)} · ${defaultModel}`,
          api: providerApi(defaultProvider, 'OpenAI Chat Completions'),
          source: defaultSource,
          edit: defaultEdit,
          editPanel: 'defaults',
        },
      ],
    },
  ]
}

function CollapsiblePanel({
  id,
  icon,
  title,
  summary,
  open,
  onToggle,
  children,
}: {
  id: PanelId
  icon: ReactNode
  title: string
  summary: ReactNode
  open: boolean
  onToggle: () => void
  children: ReactNode
}) {
  return (
    <div className="card card-padding" id={`llm-panel-${id}`} style={{ scrollMarginTop: 80 }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          width: '100%',
          padding: 0,
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          color: 'inherit',
          font: 'inherit',
        }}
      >
        <span style={{ color: 'var(--accent-copper)', display: 'flex', paddingTop: 2 }}>{icon}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <h3 className="card-title">{title}</h3>
          {!open && <span className="text-sm text-muted" style={{ display: 'block', marginTop: 6 }}>{summary}</span>}
        </span>
        <ChevronDown
          size={18}
          style={{
            color: 'var(--text-muted)',
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'none',
            flexShrink: 0,
            marginTop: 2,
          }}
        />
      </button>
      {open && <div style={{ marginTop: 20 }}>{children}</div>}
    </div>
  )
}

export default function LLMSettingsPage() {
  const [llmState, setLlmState] = useState<{
    status: 'loading' | 'ready' | 'error'
    settings: LlmSettings | null
    message?: string
  }>({ status: 'loading', settings: null })

  const [openPanels, setOpenPanels] = useState<Record<PanelId, boolean>>({
    modes: false,
    defaults: false,
    modD: false,
    catalog: false,
    credit: false,
  })
  const togglePanel = (id: PanelId) => setOpenPanels((p) => ({ ...p, [id]: !p[id] }))
  const openPanel = (id: PanelId) => {
    setOpenPanels((p) => ({ ...p, [id]: true }))
    requestAnimationFrame(() => {
      document.getElementById(`llm-panel-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  const [providerDraft, setProviderDraft] = useState<LlmProvider>('openai')
  const [modelDraft, setModelDraft] = useState('')
  const [saveStatus, setSaveStatus] = useState<{
    status: 'idle' | 'saving' | 'saved' | 'error'
    message?: string
  }>({ status: 'idle' })

  const [catalogProvider, setCatalogProvider] = useState<LlmProvider>('openai')
  const [newModelName, setNewModelName] = useState('')
  const [newInputPer1M, setNewInputPer1M] = useState('')
  const [newOutputPer1M, setNewOutputPer1M] = useState('')
  const [catalogStatus, setCatalogStatus] = useState<{
    status: 'idle' | 'saving' | 'error'
    message?: string
  }>({ status: 'idle' })

  // Mod D-specific settings
  const [modDDraft, setModDDraft] = useState<ModDLlmConfig>({
    extraction_provider: 'openai',
    extraction_model: '',
    grading_provider: 'openai',
    grading_model: '',
  })
  const [modDSaveStatus, setModDSaveStatus] = useState<{
    status: 'idle' | 'saving' | 'saved' | 'error'
    message?: string
  }>({ status: 'idle' })

  const [creditCfg, setCreditCfg] = useState<ReadCreditConfig | null>(null)
  const [creditDraft, setCreditDraft] = useState({ reads_per_usd: '', min_credits_per_paper: '', max_credits_per_paper: '' })
  const [creditStatus, setCreditStatus] = useState<{ status: 'idle' | 'saving' | 'saved' | 'error'; message?: string }>({ status: 'idle' })

  const applyLlmSettings = (settings: LlmSettings) => {
    setLlmState({ status: 'ready', settings })
    const provider = settings.default_provider
    const providerSettings = settings.providers[provider]
    const initialModel = providerSettings.current_model || providerSettings.models[0] || ''
    setProviderDraft(provider)
    setCatalogProvider(provider)
    setModelDraft(initialModel)
    if (settings.mod_d) {
      setModDDraft(settings.mod_d)
    }
  }

  useEffect(() => {
    fetchLlmSettings()
      .then((settings) => applyLlmSettings(settings))
      .catch((err) => {
        setLlmState({ status: 'error', settings: null, message: extractError(err, 'LLM ayarları yüklenemedi') })
      })
    fetchReadCreditConfig()
      .then((cfg) => {
        setCreditCfg(cfg)
        setCreditDraft({
          reads_per_usd: String(cfg.reads_per_usd),
          min_credits_per_paper: String(cfg.min_credits_per_paper),
          max_credits_per_paper: String(cfg.max_credits_per_paper),
        })
      })
      .catch(() => {})
  }, [])

  const providerSettings = llmState.settings?.providers[providerDraft] || null
  const modelOptions = useMemo(() => {
    if (!providerSettings) return [] as string[]
    if (providerSettings.models.length > 0) return providerSettings.models
    return providerSettings.current_model ? [providerSettings.current_model] : []
  }, [providerSettings])

  const catalogEntries = useMemo(() => {
    const settings = llmState.settings?.providers[catalogProvider]
    return settings?.model_entries || []
  }, [llmState.settings, catalogProvider])

  const modeGuide = useMemo(
    () => (llmState.settings ? buildModeGuide(llmState.settings) : []),
    [llmState.settings],
  )

  const modelsForProvider = (provider: LlmProvider): string[] => {
    const ps = llmState.settings?.providers[provider]
    if (!ps) return []
    return ps.models.length > 0 ? ps.models : ps.current_model ? [ps.current_model] : []
  }

  const onProviderChange = (provider: LlmProvider) => {
    setProviderDraft(provider)
    const settings = llmState.settings?.providers[provider]
    if (!settings) {
      setModelDraft('')
      return
    }
    const nextModel = settings.models[0] || settings.current_model || ''
    setModelDraft(nextModel)
    setSaveStatus({ status: 'idle' })
  }

  const onSaveLlmSettings = async () => {
    if (!modelDraft) {
      setSaveStatus({ status: 'error', message: 'Lütfen bir model seçin.' })
      return
    }
    setSaveStatus({ status: 'saving' })
    try {
      const updated = await updateLlmSettings(providerDraft, modelDraft)
      applyLlmSettings(updated)
      const nextModel = updated.providers[providerDraft].current_model || modelDraft
      setModelDraft(nextModel)
      setSaveStatus({ status: 'saved', message: 'LLM varsayılanları güncellendi.' })
    } catch (err) {
      setSaveStatus({ status: 'error', message: extractError(err, 'LLM ayarları güncellenemedi') })
    }
  }

  const onSaveModDSettings = async () => {
    if (!modDDraft.extraction_model) {
      setModDSaveStatus({ status: 'error', message: 'Soru çıkarma modeli gerekli.' })
      return
    }
    if (!modDDraft.grading_model) {
      setModDSaveStatus({ status: 'error', message: 'Notlama modeli gerekli.' })
      return
    }
    setModDSaveStatus({ status: 'saving' })
    try {
      const updated = await updateModDLlmSettings(modDDraft)
      applyLlmSettings(updated)
      setModDSaveStatus({ status: 'saved', message: 'Mod D ayarları güncellendi.' })
    } catch (err) {
      setModDSaveStatus({ status: 'error', message: extractError(err, 'Mod D ayarları kaydedilemedi') })
    }
  }

  const onAddModelToCatalog = async () => {
    const model = newModelName.trim()
    if (!model) {
      setCatalogStatus({ status: 'error', message: 'Model adı gerekli.' })
      return
    }

    const inputPer1M = Number(newInputPer1M)
    const outputPer1M = Number(newOutputPer1M)
    if (!Number.isFinite(inputPer1M) || !Number.isFinite(outputPer1M)) {
      setCatalogStatus({ status: 'error', message: 'Giriş/çıkış fiyatı sayısal olmalı.' })
      return
    }

    setCatalogStatus({ status: 'saving' })
    try {
      const updated = await upsertLlmModel({
        provider: catalogProvider,
        model,
        input_per_1m: inputPer1M,
        output_per_1m: outputPer1M,
      })
      applyLlmSettings(updated)
      setNewModelName('')
      setNewInputPer1M('')
      setNewOutputPer1M('')
      setCatalogStatus({ status: 'idle' })
    } catch (err) {
      setCatalogStatus({ status: 'error', message: extractError(err, 'Model eklenemedi') })
    }
  }

  const onDeleteModelFromCatalog = async (provider: LlmProvider, model: string) => {
    setCatalogStatus({ status: 'saving' })
    try {
      const updated = await deleteLlmModel(provider, model)
      applyLlmSettings(updated)
      setCatalogStatus({ status: 'idle' })
    } catch (err) {
      setCatalogStatus({ status: 'error', message: extractError(err, 'Model silinemedi') })
    }
  }

  const onSaveCreditConfig = async () => {
    const rpu = Number(creditDraft.reads_per_usd)
    const min = Number(creditDraft.min_credits_per_paper)
    const max = Number(creditDraft.max_credits_per_paper)
    if (!Number.isFinite(rpu) || rpu <= 0) {
      setCreditStatus({ status: 'error', message: 'Okuma/$ kur değeri 0\'dan büyük olmalı.' })
      return
    }
    if (!Number.isFinite(min) || min <= 0 || !Number.isFinite(max) || max <= 0) {
      setCreditStatus({ status: 'error', message: 'Min/maks değerler 0\'dan büyük sayı olmalı.' })
      return
    }
    if (min >= max) {
      setCreditStatus({ status: 'error', message: 'Minimum, maksimumdan küçük olmalı.' })
      return
    }
    setCreditStatus({ status: 'saving' })
    try {
      const updated = await updateReadCreditConfig({ reads_per_usd: rpu, min_credits_per_paper: min, max_credits_per_paper: max })
      setCreditCfg(updated)
      setCreditStatus({ status: 'saved', message: 'Kredi ayarları kaydedildi.' })
    } catch (err) {
      setCreditStatus({ status: 'error', message: extractError(err, 'Kredi ayarları kaydedilemedi') })
    }
  }

  const settings = llmState.settings
  const loadingSummary = llmState.status === 'error' ? llmState.message : 'Yükleniyor…'

  const defaultsSummary = settings?.effective
    ? `${providerName(settings.effective.provider)} · ${settings.effective.model} — ${sourceLabel(settings.effective.source)}`
    : loadingSummary

  const modDSummary = settings?.mod_d
    ? `Soru çıkarma: ${providerName(settings.mod_d.extraction_provider)} · ${settings.mod_d.extraction_model} — Notlama: ${providerName(settings.mod_d.grading_provider)} · ${settings.mod_d.grading_model}`
    : loadingSummary

  const modesSummary = settings
    ? `Yeni sınavlar Mod D ile oluşturulur. ${modDSummary}. Mod A/B, C ve sınıf analizi: varsayılanlar.`
    : loadingSummary

  const catalogSummary = settings
    ? `OpenAI: ${settings.providers.openai.model_entries?.length ?? 0} model · Gemini: ${settings.providers.gemini.model_entries?.length ?? 0} model`
    : loadingSummary

  const creditSummary = creditCfg
    ? `1 $ = ${creditCfg.reads_per_usd} okuma · kağıt başına ${creditCfg.min_credits_per_paper}–${creditCfg.max_credits_per_paper} kredi`
    : 'Yükleniyor…'

  const overview: { id: PanelId; title: string; text: string }[] = [
    { id: 'modes', title: 'Modlar ve kullanılan modeller', text: 'Her modun hangi adımda hangi sağlayıcı/modeli kullandığı, bu bilginin nereden okunduğu ve nereden değiştirileceği (salt okunur).' },
    { id: 'defaults', title: 'Varsayılanlar', text: 'Varsayılan sağlayıcı ve model. Mod A/B, Mod C ve sınıf analizi bunları kullanır.' },
    { id: 'modD', title: 'Mod D ayarları', text: 'Mod D soru çıkarma ve notlama için ayrı sağlayıcı/model. Diğer modları etkilemez.' },
    { id: 'catalog', title: 'Model kataloğu', text: 'Seçilebilir modeller ve maliyet hesabında kullanılan token fiyatları.' },
    { id: 'credit', title: 'Okuma kredisi fiyatlandırma', text: 'AI maliyetinin okuma kredisine çevrilmesi ve kağıt başına min/maks kredi.' },
  ]

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <h2 className="page-header-title">LLM Ayarları</h2>
        <p className="page-header-sub">Yapay zeka model varsayılanlarını yapılandırın ve model kataloğunu yönetin</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Overview of what can be configured here */}
        <div className="card card-padding">
          <div className="flex items-center gap-8 mb-12">
            <Info size={18} style={{ color: 'var(--accent-copper)' }} />
            <h3 className="card-title">Bu ekranda neler ayarlanır?</h3>
          </div>
          <p className="text-sm text-muted mb-12">
            Ayarlar uygulama ayarlarına (veritabanı) kaydedilir ve .env değerlerinin önüne geçer. Kaydedilmemiş bir ayar için .env, o da yoksa kod varsayılanı kullanılır.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {overview.map((item) => (
              <li key={item.id} className="text-sm">
                <button
                  type="button"
                  onClick={() => openPanel(item.id)}
                  style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', fontWeight: 600, color: 'var(--accent-copper)', font: 'inherit' }}
                >
                  {item.title}
                </button>
                <span className="text-muted"> — {item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Per-mode guide (read-only, derived from live settings) */}
        <CollapsiblePanel
          id="modes"
          icon={<Layers size={18} />}
          title="Modlar ve kullanılan modeller"
          summary={modesSummary}
          open={openPanels.modes}
          onToggle={() => togglePanel('modes')}
        >
          {!settings ? (
            <p className="text-sm text-muted">{loadingSummary}</p>
          ) : (
            <div className="flex flex-col gap-24">
              {modeGuide.map((mode) => (
                <div key={mode.key}>
                  <div className="flex items-center gap-8 mb-4">
                    <span style={{ color: 'var(--accent-copper)', display: 'flex' }}>{mode.icon}</span>
                    <span style={{ fontWeight: 700 }}>{mode.name}</span>
                  </div>
                  <p className="text-sm text-muted mb-12">{mode.description}</p>
                  <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--border-color)', borderRadius: 8 }}>
                    {mode.rows.map((row, index) => (
                      <div
                        key={row.step}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                          gap: '8px 20px',
                          padding: '12px 14px',
                          borderTop: index === 0 ? 'none' : '1px solid var(--border-color)',
                        }}
                      >
                        <div>
                          <div className="text-xs text-muted">{row.step}</div>
                          <div className="text-sm" style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{row.value}</div>
                          {row.api && <div className="text-xs text-muted">{row.api}</div>}
                        </div>
                        <div>
                          <div className="text-xs text-muted">Nereden okunur</div>
                          <div className="text-xs" style={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>{row.source}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted">Nasıl değiştirilir</div>
                          <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            {row.edit}
                            {row.editPanel && (
                              <>
                                {' '}
                                <button
                                  type="button"
                                  onClick={() => openPanel(row.editPanel!)}
                                  style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', fontWeight: 600, color: 'var(--accent-copper)', font: 'inherit' }}
                                >
                                  Aç →
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CollapsiblePanel>

        {/* Default provider + model */}
        <CollapsiblePanel
          id="defaults"
          icon={<Cpu size={18} />}
          title="Varsayılanlar"
          summary={defaultsSummary}
          open={openPanels.defaults}
          onToggle={() => togglePanel('defaults')}
        >
          <p className="text-sm text-muted mb-16">
            Mod A/B notlama, sınıf analizi ve Mod A/B soru çıkarma (sonradan yükleme) için kullanılır. Kaydedilen model, seçilen sağlayıcının modeli olur: OpenAI modeli Mod C notlamada, Gemini modeli Mod A/B rubrik çıkarmada da kullanılır. Mod D için ayrı panele bakın.
          </p>

          {llmState.status === 'loading' && (
            <p className="text-sm text-muted">LLM ayarları yükleniyor...</p>
          )}
          {llmState.status === 'error' && (
            <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{llmState.message}</p>
          )}

          {llmState.status === 'ready' && settings && (
            <div className="flex flex-col gap-16">
              <div className="form-group">
                <label className="form-label">Varsayılan Sağlayıcı</label>
                <select
                  className="form-select"
                  value={providerDraft}
                  onChange={(e) => onProviderChange(e.target.value as LlmProvider)}
                >
                  <option value="openai" disabled={!settings.providers.openai.has_api_key}>
                    OpenAI{settings.providers.openai.has_api_key ? '' : ' (API anahtarı eksik)'}
                  </option>
                  <option value="gemini" disabled={!settings.providers.gemini.has_api_key}>
                    Google Gemini{settings.providers.gemini.has_api_key ? '' : ' (API anahtarı eksik)'}
                  </option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Varsayılan Model</label>
                <select
                  className="form-select"
                  value={modelDraft}
                  onChange={(e) => {
                    setModelDraft(e.target.value)
                    setSaveStatus({ status: 'idle' })
                  }}
                  disabled={modelOptions.length === 0}
                >
                  {modelOptions.length === 0 && <option value="">Ortamda model bulunamadı</option>}
                  {modelOptions.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              {settings.effective && (
                <p className="text-xs text-muted">
                  Etkin model: {settings.effective.model} ({sourceLabel(settings.effective.source)})
                </p>
              )}

              <div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={onSaveLlmSettings}
                  disabled={saveStatus.status === 'saving'}
                >
                  {saveStatus.status === 'saving' ? 'Kaydediliyor...' : 'Varsayılanları Kaydet'}
                </button>
                {saveStatus.status === 'saved' && (
                  <span className="text-sm ml-12" style={{ color: 'var(--color-success)' }}>
                    {saveStatus.message}
                  </span>
                )}
                {saveStatus.status === 'error' && (
                  <span className="text-sm ml-12" style={{ color: 'var(--color-danger)' }}>
                    {saveStatus.message}
                  </span>
                )}
              </div>
            </div>
          )}
        </CollapsiblePanel>

        {/* Mod D LLM settings */}
        <CollapsiblePanel
          id="modD"
          icon={<Zap size={18} />}
          title="Mod D Ayarları"
          summary={modDSummary}
          open={openPanels.modD}
          onToggle={() => togglePanel('modD')}
        >
          <p className="text-sm text-muted mb-20">
            Mod D soru çıkarma ve notlama için sağlayıcı ve model seçin. Diğer modları etkilemez.
          </p>

          {llmState.status === 'ready' && settings && (
            <div className="flex flex-col gap-20">
              {/* Extraction */}
              <div>
                <p className="text-sm font-semibold mb-12" style={{ color: 'var(--text-secondary)' }}>Soru Çıkarma</p>
                <div className="flex gap-12">
                  <div className="form-group" style={{ flex: '0 0 160px' }}>
                    <label className="form-label">Sağlayıcı</label>
                    <select
                      className="form-select"
                      value={modDDraft.extraction_provider}
                      onChange={(e) => {
                        const p = e.target.value as LlmProvider
                        const firstModel = modelsForProvider(p)[0] || ''
                        setModDDraft((d) => ({ ...d, extraction_provider: p, extraction_model: firstModel }))
                        setModDSaveStatus({ status: 'idle' })
                      }}
                    >
                      <option value="openai" disabled={!settings.providers.openai.has_api_key}>
                        OpenAI{settings.providers.openai.has_api_key ? '' : ' (anahtar eksik)'}
                      </option>
                      <option value="gemini" disabled={!settings.providers.gemini.has_api_key}>
                        Gemini{settings.providers.gemini.has_api_key ? '' : ' (anahtar eksik)'}
                      </option>
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Model</label>
                    <select
                      className="form-select"
                      value={modDDraft.extraction_model}
                      onChange={(e) => {
                        setModDDraft((d) => ({ ...d, extraction_model: e.target.value }))
                        setModDSaveStatus({ status: 'idle' })
                      }}
                    >
                      {modelsForProvider(modDDraft.extraction_provider).map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                      {modelsForProvider(modDDraft.extraction_provider).length === 0 && (
                        <option value={modDDraft.extraction_model}>{modDDraft.extraction_model || 'Model yok'}</option>
                      )}
                    </select>
                  </div>
                </div>
              </div>

              {/* Grading */}
              <div>
                <p className="text-sm font-semibold mb-12" style={{ color: 'var(--text-secondary)' }}>Notlama</p>
                <div className="flex gap-12">
                  <div className="form-group" style={{ flex: '0 0 160px' }}>
                    <label className="form-label">Sağlayıcı</label>
                    <select
                      className="form-select"
                      value={modDDraft.grading_provider}
                      onChange={(e) => {
                        const p = e.target.value as LlmProvider
                        const firstModel = modelsForProvider(p)[0] || ''
                        setModDDraft((d) => ({ ...d, grading_provider: p, grading_model: firstModel }))
                        setModDSaveStatus({ status: 'idle' })
                      }}
                    >
                      <option value="openai" disabled={!settings.providers.openai.has_api_key}>
                        OpenAI{settings.providers.openai.has_api_key ? '' : ' (anahtar eksik)'}
                      </option>
                      <option value="gemini" disabled={!settings.providers.gemini.has_api_key}>
                        Gemini{settings.providers.gemini.has_api_key ? '' : ' (anahtar eksik)'}
                      </option>
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Model</label>
                    <select
                      className="form-select"
                      value={modDDraft.grading_model}
                      onChange={(e) => {
                        setModDDraft((d) => ({ ...d, grading_model: e.target.value }))
                        setModDSaveStatus({ status: 'idle' })
                      }}
                    >
                      {modelsForProvider(modDDraft.grading_provider).map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                      {modelsForProvider(modDDraft.grading_provider).length === 0 && (
                        <option value={modDDraft.grading_model}>{modDDraft.grading_model || 'Model yok'}</option>
                      )}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={onSaveModDSettings}
                  disabled={modDSaveStatus.status === 'saving'}
                >
                  {modDSaveStatus.status === 'saving' ? 'Kaydediliyor...' : 'Mod D Ayarlarını Kaydet'}
                </button>
                {modDSaveStatus.status === 'saved' && (
                  <span className="text-sm ml-12" style={{ color: 'var(--color-success)' }}>
                    {modDSaveStatus.message}
                  </span>
                )}
                {modDSaveStatus.status === 'error' && (
                  <span className="text-sm ml-12" style={{ color: 'var(--color-danger)' }}>
                    {modDSaveStatus.message}
                  </span>
                )}
              </div>
            </div>
          )}
        </CollapsiblePanel>

        {/* Model catalog */}
        <CollapsiblePanel
          id="catalog"
          icon={<BookOpen size={18} />}
          title="Model Kataloğu"
          summary={catalogSummary}
          open={openPanels.catalog}
          onToggle={() => togglePanel('catalog')}
        >
          <div className="form-group mb-16">
            <label className="form-label">Sağlayıcı</label>
            <select
              className="form-select"
              value={catalogProvider}
              onChange={(e) => setCatalogProvider(e.target.value as LlmProvider)}
              style={{ maxWidth: 200 }}
            >
              <option value="openai">OpenAI</option>
              <option value="gemini">Gemini</option>
            </select>
          </div>

          <div className="table-wrapper mb-16">
            <table className="table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th style={{ textAlign: 'right' }}>Giriş $/1M</th>
                  <th style={{ textAlign: 'right' }}>Çıkış $/1M</th>
                  <th>Kaynak</th>
                  <th style={{ width: 32 }}></th>
                </tr>
              </thead>
              <tbody>
                {catalogEntries.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-sm text-muted">
                      Bu sağlayıcı için henüz model yok.
                    </td>
                  </tr>
                )}
                {catalogEntries.map((entry) => (
                  <tr key={`${catalogProvider}-${entry.model}`}>
                    <td className="text-sm font-semibold">{entry.model}</td>
                    <td style={{ textAlign: 'right' }} className="text-sm">
                      {entry.input_per_1m != null ? `$${Number(entry.input_per_1m).toFixed(2)}` : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }} className="text-sm">
                      {entry.output_per_1m != null ? `$${Number(entry.output_per_1m).toFixed(2)}` : '-'}
                    </td>
                    <td className="text-sm text-muted">{sourceLabel(entry.source)}</td>
                    <td>
                      {entry.source === 'app_settings' ? (
                        <button
                          className="btn btn-icon btn-ghost"
                          onClick={() => onDeleteModelFromCatalog(catalogProvider, entry.model)}
                          disabled={catalogStatus.status === 'saving'}
                          title="Kaldır"
                        >
                          <X size={14} />
                        </button>
                      ) : (
                        <span className="text-xs text-muted">Salt okunur</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add row */}
          <div className="flex gap-8 items-end">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Model</label>
              <input
                type="text"
                className="form-input"
                placeholder={catalogProvider === 'openai' ? 'gpt-4o' : 'gemini-2.0-flash'}
                value={newModelName}
                onChange={(e) => setNewModelName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onAddModelToCatalog()}
              />
            </div>
            <div className="form-group" style={{ flex: '0 0 90px' }}>
              <label className="form-label">Giriş $/1M</label>
              <input
                type="number"
                className="form-input"
                placeholder="2.50"
                value={newInputPer1M}
                onChange={(e) => setNewInputPer1M(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ flex: '0 0 90px' }}>
              <label className="form-label">Çıkış $/1M</label>
              <input
                type="number"
                className="form-input"
                placeholder="10.00"
                value={newOutputPer1M}
                onChange={(e) => setNewOutputPer1M(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ flex: '0 0 auto', marginBottom: 0 }}>
              <button
                className="btn btn-outline btn-sm"
                onClick={onAddModelToCatalog}
                disabled={catalogStatus.status === 'saving'}
                style={{ marginTop: 22 }}
              >
                <Plus size={14} />
                Ekle
              </button>
            </div>
          </div>
          {catalogStatus.status === 'error' && (
            <p className="text-sm mt-8" style={{ color: 'var(--color-danger)' }}>{catalogStatus.message}</p>
          )}
        </CollapsiblePanel>

        {/* Read-credit pricing config */}
        <CollapsiblePanel
          id="credit"
          icon={<CreditCard size={18} />}
          title="Okuma Kredisi Fiyatlandırma"
          summary={creditSummary}
          open={openPanels.credit}
          onToggle={() => togglePanel('credit')}
        >
          <p className="text-sm text-muted mb-16">
            1 okuma kredisinin kaç dolarlık AI maliyetine eşit olduğunu ve işlem başına min/maks kredi sınırlarını belirler.
          </p>

          {creditCfg === null ? (
            <p className="text-sm text-muted">Yükleniyor...</p>
          ) : (
            <div className="flex flex-col gap-16">
              <div className="flex gap-12">
                <div className="form-group" style={{ flex: '0 0 130px' }}>
                  <label className="form-label">Okuma / $</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    className="form-input"
                    placeholder="7"
                    value={creditDraft.reads_per_usd}
                    onChange={(e) => { setCreditDraft((d) => ({ ...d, reads_per_usd: e.target.value })); setCreditStatus({ status: 'idle' }) }}
                  />
                  <span className="text-xs text-muted">1 $ = bu kadar okuma</span>
                </div>
                <div className="form-group" style={{ flex: '0 0 110px' }}>
                  <label className="form-label">Min kredi</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    className="form-input"
                    placeholder="0.7"
                    value={creditDraft.min_credits_per_paper}
                    onChange={(e) => { setCreditDraft((d) => ({ ...d, min_credits_per_paper: e.target.value })); setCreditStatus({ status: 'idle' }) }}
                  />
                </div>
                <div className="form-group" style={{ flex: '0 0 110px' }}>
                  <label className="form-label">Maks kredi</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    className="form-input"
                    placeholder="2.0"
                    value={creditDraft.max_credits_per_paper}
                    onChange={(e) => { setCreditDraft((d) => ({ ...d, max_credits_per_paper: e.target.value })); setCreditStatus({ status: 'idle' }) }}
                  />
                </div>
              </div>

              <div className="flex items-center gap-12">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={onSaveCreditConfig}
                  disabled={creditStatus.status === 'saving'}
                >
                  {creditStatus.status === 'saving' ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
                {creditStatus.status === 'saved' && (
                  <span className="text-sm" style={{ color: 'var(--color-success)' }}>{creditStatus.message}</span>
                )}
                {creditStatus.status === 'error' && (
                  <span className="text-sm" style={{ color: 'var(--color-danger)' }}>{creditStatus.message}</span>
                )}
              </div>
            </div>
          )}
        </CollapsiblePanel>
      </div>
    </div>
  )
}
